import {Link, useSearchParams} from 'react-router-dom';
import {format} from 'date-fns';
import {formatCurrency} from '@common/lib/utils';
import {
  FileText,
  ChevronRight,
  Calendar,
  CheckCircle,
  Clock,
  Receipt,
  DollarSign,
  Users,
  AlertCircle,
  Package,
} from 'lucide-react';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Badge} from '@common-web/components/ui/badge';
import {Button} from '@common-web/components/ui/button';
import {PageHeader} from '@common-web/components/page-header';
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@common-web/components/ui/tabs';
import {DataTable, useDataTable, type Column} from '@common-web/components/data-table';
import {trpc, RouterOutput} from '@common-web/lib/trpc';
import {GenerationRuns} from '@internal/pages/invoices/generation-runs';

type Invoice = RouterOutput['internal']['invoice']['list']['invoices'][number];

type InvoiceFilters = {status: string; clientId: string};

const formatDate = (date: Date | null | undefined) => {
  if (!date) return 'N/A';
  try {
    return format(new Date(date), 'MM/dd/yyyy');
  } catch {
    return 'N/A';
  }
};

const getBalance = (invoice: Invoice) => {
  return invoice.amountDue - invoice.paymentPosted;
};

export function InvoiceList() {
  const [searchParams, setSearchParams] = useSearchParams();

  const table = useDataTable<InvoiceFilters>({
    defaultPageSize: 20,
    defaultFilters: {status: '', clientId: ''},
  });

  const {data: clientsData} = trpc.internal.invoice.getClients.useQuery();

  const {data, isLoading, isFetching, error} = trpc.internal.invoice.list.useQuery({
    page: table.page,
    pageSize: table.pageSize,
    search: table.debouncedSearch || undefined,
    status: table.filterValues.status || undefined,
    clientId: table.filterValues.clientId || undefined,
  });

  const getStatusBadge = (invoice: Invoice) => {
    if (invoice.invoiceStatus === 'OPEN') {
      const isPastDue = invoice.dueDate && new Date(invoice.dueDate) < new Date();
      return isPastDue ? (
        <Badge className='bg-red-600 text-white border-0 font-medium'>
          <AlertCircle className='h-3 w-3 mr-1' />
          Past Due
        </Badge>
      ) : (
        <Badge className='bg-amber text-white border-0 font-medium'>
          <Clock className='h-3 w-3 mr-1' />
          Open
        </Badge>
      );
    }

    if (invoice.invoiceStatus === 'CLOSED') {
      return (
        <Badge className='bg-emerald text-white border-0 font-medium'>
          <CheckCircle className='h-3 w-3 mr-1' />
          Closed
        </Badge>
      );
    }

    return (
      <Badge className='bg-brand-gray/10 text-brand-gray border-brand-gray/20'>
        {invoice.invoiceStatus || 'Unknown'}
      </Badge>
    );
  };

  const columns: Column<Invoice>[] = [
    {
      id: 'invoiceNumber',
      header: 'Invoice #',
      render: (invoice) => (
        <Link
          to={`/invoices/${invoice.invoiceKey}`}
          className='font-medium text-brand-blue hover:text-brand-blue-dark hover:underline'
        >
          {invoice.invoiceNumber || `#${invoice.invoiceKey}`}
        </Link>
      ),
    },
    {
      id: 'groupName',
      header: 'Group',
      render: (invoice) => (
        <div>
          <div className='font-medium text-brand-navy'>{invoice.groupName}</div>
          <div className='text-xs text-brand-gray font-mono'>{invoice.groupId}</div>
        </div>
      ),
    },
    {
      id: 'clientName',
      header: 'Client',
      render: (invoice) => (
        <span className='text-sm text-brand-gray'>{invoice.clientName || '-'}</span>
      ),
    },
    {
      id: 'invoiceDate',
      header: 'Invoice Date',
      render: (invoice) => (
        <div className='flex items-center gap-2'>
          <Calendar className='h-4 w-4 text-brand-gray' />
          <span className='text-sm'>{formatDate(invoice.invoiceDate)}</span>
        </div>
      ),
    },
    {
      id: 'dueDate',
      header: 'Due Date',
      render: (invoice) => {
        const isPastDue =
          invoice.dueDate &&
          new Date(invoice.dueDate) < new Date() &&
          invoice.invoiceStatus === 'OPEN';
        return (
          <div className='flex items-center gap-2'>
            <Calendar className='h-4 w-4 text-brand-gray' />
            <span className={`text-sm ${isPastDue ? 'text-red-600 font-semibold' : ''}`}>
              {formatDate(invoice.dueDate)}
            </span>
          </div>
        );
      },
    },
    {
      id: 'premium',
      header: 'Premium',
      render: (invoice) => (
        <div className='font-mono font-medium text-brand-navy'>
          {formatCurrency(invoice.premium)}
        </div>
      ),
    },
    {
      id: 'amountDue',
      header: 'Amount Due',
      render: (invoice) => (
        <div className='font-mono font-medium text-brand-navy'>
          {formatCurrency(invoice.amountDue)}
        </div>
      ),
    },
    {
      id: 'balance',
      header: 'Balance',
      render: (invoice) => {
        const balance = getBalance(invoice);
        return (
          <div
            className={`font-mono font-semibold ${balance > 0 ? 'text-red-600' : 'text-emerald'}`}
          >
            {formatCurrency(balance)}
          </div>
        );
      },
    },
    {
      id: 'status',
      header: 'Status',
      render: (invoice) => getStatusBadge(invoice),
    },
    {
      id: 'actions',
      header: '',
      render: (invoice) => (
        <Link to={`/invoices/${invoice.invoiceKey}`}>
          <Button
            variant='outline'
            size='sm'
            className='border-brand-blue/20 text-brand-blue hover:bg-brand-blue/5'
          >
            View
            <ChevronRight className='h-4 w-4 ml-1' />
          </Button>
        </Link>
      ),
    },
  ];

  const activeTab = searchParams.get('tab') || 'invoices';

  const handleTabChange = (value: string) => {
    setSearchParams((prev) => {
      if (value === 'invoices') {
        prev.delete('tab');
      } else {
        prev.set('tab', value);
      }
      return prev;
    });
  };

  return (
    <div className='min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-blue/5'>
      <div className='flex flex-col gap-0'>
        {/* Header */}
        <PageHeader
          title='Invoices'
          description='View and manage premium invoices across all groups'
          icon={<Receipt className='h-6 w-6' />}
          isFetching={isFetching && activeTab === 'invoices'}
          isLoading={isLoading}
        />

        {/* Main Content */}
        <div className='px-8 py-6 max-w-7xl mx-auto w-full'>
          <Tabs value={activeTab} onValueChange={handleTabChange} className='mb-6'>
            <TabsList className='bg-gray-100 p-1'>
              <TabsTrigger
                value='invoices'
                className='data-[state=active]:bg-white data-[state=active]:text-brand-navy data-[state=active]:shadow-sm'
              >
                <Receipt className='h-4 w-4 mr-2' />
                Invoices
              </TabsTrigger>
              <TabsTrigger
                value='generation'
                className='data-[state=active]:bg-white data-[state=active]:text-brand-navy data-[state=active]:shadow-sm'
              >
                <Package className='h-4 w-4 mr-2' />
                Generation Runs
              </TabsTrigger>
            </TabsList>

            <TabsContent value='generation' className='mt-6'>
              <GenerationRuns />
            </TabsContent>

            <TabsContent value='invoices' className='mt-6'>
              {/* Summary Cards */}
              {data && (
                <div className='grid grid-cols-1 md:grid-cols-4 gap-4 mb-6'>
                  <Card className='border-gray-200 shadow-sm hover:shadow-md transition-shadow'>
                    <CardHeader className='pb-3'>
                      <CardTitle className='text-sm font-medium text-brand-gray flex items-center justify-between'>
                        Total Results
                        <FileText className='h-5 w-5 text-brand-blue' />
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className='text-3xl font-bold text-brand-navy'>{data.totalCount}</div>
                      <p className='text-xs text-brand-gray mt-1'>Invoices found</p>
                    </CardContent>
                  </Card>

                  <Card className='border-gray-200 shadow-sm hover:shadow-md transition-shadow'>
                    <CardHeader className='pb-3'>
                      <CardTitle className='text-sm font-medium text-brand-gray flex items-center justify-between'>
                        Total Premium
                        <DollarSign className='h-5 w-5 text-emerald' />
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className='text-2xl font-bold text-brand-navy'>
                        {formatCurrency(data.invoices.reduce((sum, inv) => sum + inv.premium, 0))}
                      </div>
                      <p className='text-xs text-brand-gray mt-1'>This page</p>
                    </CardContent>
                  </Card>

                  <Card className='border-gray-200 shadow-sm hover:shadow-md transition-shadow'>
                    <CardHeader className='pb-3'>
                      <CardTitle className='text-sm font-medium text-brand-gray flex items-center justify-between'>
                        Outstanding
                        <AlertCircle className='h-5 w-5 text-red-500' />
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className='text-2xl font-bold text-red-600'>
                        {formatCurrency(
                          data.invoices.reduce((sum, inv) => sum + getBalance(inv), 0),
                        )}
                      </div>
                      <p className='text-xs text-brand-gray mt-1'>Balance due</p>
                    </CardContent>
                  </Card>

                  <Card className='border-gray-200 shadow-sm hover:shadow-md transition-shadow'>
                    <CardHeader className='pb-3'>
                      <CardTitle className='text-sm font-medium text-brand-gray flex items-center justify-between'>
                        Subscribers
                        <Users className='h-5 w-5 text-brand-blue' />
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className='text-3xl font-bold text-brand-navy'>
                        {data.invoices.reduce((sum, inv) => sum + inv.subscriberCount, 0)}
                      </div>
                      <p className='text-xs text-brand-gray mt-1'>This page</p>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Invoices Table */}
              <DataTable<Invoice, InvoiceFilters>
                table={table}
                columns={columns}
                data={data?.invoices}
                total={data?.totalCount}
                totalPages={data?.totalPages}
                loading={isLoading}
                error={error}
                emptyMessage='No invoices found'
                title='Invoice List'
                entity={{singular: 'invoice', plural: 'invoices'}}
                searchPlaceholder='Search by invoice number or group ID...'
                filters={[
                  {
                    key: 'clientId',
                    label: 'Clients',
                    type: 'select',
                    placeholder: 'All Clients',
                    options: (clientsData?.clients ?? []).map((client) => ({
                      value: client.id,
                      label: client.name,
                    })),
                  },
                  {
                    key: 'status',
                    label: 'Status',
                    type: 'select',
                    placeholder: 'All Status',
                    options: [
                      {value: 'OPEN', label: 'Open'},
                      {value: 'CLOSED', label: 'Closed'},
                    ],
                  },
                ]}
                rowKey={(invoice) => String(invoice.invoiceKey)}
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
